export const isRazorpayConfigured = () => {
  return !!process.env.REACT_APP_RAZORPAY_KEY_ID;
};

export const loadRazorpayScript = (keyId) => {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => {
      if (window.Razorpay) {
        resolve(true);
      } else {
        reject(new Error('Razorpay script loaded but Razorpay object not found'));
      }
    };
    script.onerror = () => {
      reject(new Error('Failed to load Razorpay script'));
    };
    document.body.appendChild(script);
  });
};

export const createRazorpayOrder = async (
  apiUrl,
  amount,
  currency,
  userTypeId,
  subscriptionPlanId,
  userEmail,
  userName,
  userPhone,
  promoCode,
  subscriptionBillingCycle = 'monthly',
  financialSupportPlanId = null
) => {
  try {
    // Ensure apiUrl doesn't have trailing slash
    const baseUrl = String(apiUrl || '').replace(/\/$/, '') || 'http://localhost:8180';
    // Call /createSubscription endpoint for subscription payments (setup fee + first subscription period)
    const response = await fetch(`${baseUrl}/api/v1/eklink/user/signUp/razorpay/createSubscription`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        amount,
        currency,
        userTypeId,
        subscriptionPlanId,
        receipt: `signup-${Date.now()}`,
        email: userEmail,
        name: userName,
        phone: userPhone,
        promoCode,
        subscriptionBillingCycle,
        // The server prices the order from the plan; it needs the support plan to know the coverage.
        financialSupportPlanId: financialSupportPlanId || null,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to create subscription');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error creating Razorpay subscription:', error);
    throw error;
  }
};

export const openRazorpayCheckout = (options) => {
  return new Promise((resolve, reject) => {
    try {
      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (response) => {
        reject(new Error(`Payment failed: ${response.error.description}`));
      });
      rzp.open();
      resolve(rzp);
    } catch (error) {
      reject(error);
    }
  });
};

export const verifyRazorpayPayment = async (apiUrl, orderId, paymentId, signature) => {
  try {
    // Ensure apiUrl doesn't have trailing slash
    const baseUrl = String(apiUrl || '').replace(/\/$/, '') || 'http://localhost:8180';
    const response = await fetch(`${baseUrl}/api/v1/eklink/user/signUp/razorpay/verifyPayment`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        orderId,
        paymentId,
        signature,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Payment verification failed');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error verifying Razorpay payment:', error);
    throw error;
  }
};

export const initializeRazorpayCheckout = (
  keyId,
  orderResult,
  amount,
  currency,
  userEmail,
  userName,
  userPhone,
  onSuccess,
  onError
) => {
  return new Promise(async (resolve, reject) => {
    try {
      console.log('🔄 Initializing Razorpay checkout...', { hasKeyId: !!keyId, hasOrderId: !!orderResult?.orderId });

      if (!keyId) {
        throw new Error('Razorpay Key ID is not configured. Please set REACT_APP_RAZORPAY_KEY_ID in your .env.local');
      }

      if (!orderResult?.orderId) {
        throw new Error('Invalid order result. Missing orderId.');
      }

      await loadRazorpayScript(keyId);
      console.log('✅ Razorpay script loaded successfully');

      // "subscription": the server created a Razorpay subscription and the first payment here is the
      // customer's authorisation of the recurring payment. "order": a one-time order.
      const isSubscription = orderResult.checkoutKind === 'subscription';

      const options = {
        key: keyId,
        ...(isSubscription
          ? { subscription_id: orderResult.orderId }
          : {
              amount: Math.round(amount * 100),
              currency: currency.toUpperCase(),
              order_id: orderResult.orderId,
            }),
        handler: async (response) => {
          try {
            console.log('✅ Razorpay handler triggered. Payment Success Response:', response);

            // Verify payment on backend
            const verificationResult = await verifyRazorpayPayment(
              process.env.REACT_APP_API_URL || 'http://localhost:8180',
              response.razorpay_order_id || response.razorpay_subscription_id,
              response.razorpay_payment_id,
              response.razorpay_signature
            );

            if (verificationResult.paymentVerified) {
              const successData = {
                ok: true,
                status: 'paid',
                statusLabel: 'Razorpay payment authorized',
                transactionId: response.razorpay_payment_id,
                orderId: response.razorpay_order_id || response.razorpay_subscription_id,
                message: `Payment of ${amount} ${currency} successful`,
              };
              console.log('✅ Payment verified successfully');
              onSuccess(successData);
              resolve(successData);
            } else {
              const error = new Error('Payment verification failed');
              console.error('❌ Payment verification failed');
              onError(error);
              reject(error);
            }
          } catch (error) {
            console.error('❌ Payment verification error:', error);
            onError(error);
            reject(error);
          }
        },
        modal: {
          ondismiss: () => {
            console.log('⚠️ User dismissed Razorpay modal');
            const error = new Error('Razorpay modal was dismissed');
            onError(error);
            reject(error);
          }
        },
        prefill: {
          email: userEmail || '',
          contact: userPhone || '',
          name: userName || '',
        },
        theme: {
          color: '#3399cc',
        },
        notes: {
          note_key_1: 'eKLink Subscription Payment',
        },
      };

      console.log('🔄 Opening Razorpay checkout modal with options:', {
        key: keyId?.substring(0, 10) + '...',
        amount: Math.round(amount * 100),
        currency,
        orderId: orderResult.orderId,
      });

      const openResult = await openRazorpayCheckout(options);
      console.log('✅ Razorpay checkout modal opened');
    } catch (error) {
      console.error('❌ Razorpay initialization error:', error);
      onError(error);
      reject(error);
    }
  });
};
